import { create } from 'zustand';
import { AuthSession, UserProfile } from '../types/auth';
import * as blueskyService from '../services/bluesky';
import { saveCredentials, getCredentials, clearCredentials } from '../utils/secureStorage';

interface AuthState {
  // 状態
  session: AuthSession | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // アクション
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>(set => ({
  // 初期状態
  session: null,
  profile: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  // ログイン
  login: async (identifier: string, password: string, rememberMe: boolean = true) => {
    set({ isLoading: true, error: null });

    try {
      const session = await blueskyService.login({ identifier, password });
      const profile = await blueskyService.getCurrentProfile();

      // ログイン成功時に認証情報を保存
      await saveCredentials(identifier, password, rememberMe);

      set({
        session,
        profile: {
          did: profile.did,
          handle: profile.handle,
          displayName: profile.displayName,
          description: profile.description,
          avatar: profile.avatar,
          banner: profile.banner,
          followsCount: profile.followsCount,
          followersCount: profile.followersCount,
          postsCount: profile.postsCount,
        },
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ログインに失敗しました';
      set({
        session: null,
        profile: null,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  // ログアウト
  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      await blueskyService.logout();
      
      // 保存された認証情報をクリア
      await clearCredentials();
      
      set({
        session: null,
        profile: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ログアウトに失敗しました';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  // 初期化（セッション復元）
  initialize: async () => {
    console.log('[authStore] Starting initialization...');
    set({ isLoading: true, error: null });

    try {
      console.log('[authStore] Attempting to resume session...');
      // まず既存のセッションを復元を試みる
      let session = await blueskyService.resumeSession();
      console.log('[authStore] Resume session result:', !!session);

      // セッションが存在しない場合、保存された認証情報で自動ログインを試みる
      if (!session) {
        console.log('[authStore] No session found, checking saved credentials...');
        const credentials = await getCredentials();
        console.log('[authStore] Saved credentials found:', !!credentials);
        if (credentials) {
          try {
            console.log('[authStore] Attempting auto-login...');
            session = await blueskyService.login({
              identifier: credentials.identifier,
              password: credentials.password,
            });
            console.log('[authStore] Auto-login successful');
          } catch (error) {
            console.error('[authStore] Auto-login failed:', error);
            // 自動ログインに失敗した場合、保存された認証情報をクリア
            await clearCredentials();
          }
        }
      }

      if (session) {
        console.log('[authStore] Session exists, fetching profile...');
        const profile = await blueskyService.getCurrentProfile();
        console.log('[authStore] Profile fetched successfully');

        set({
          session,
          profile: {
            did: profile.did,
            handle: profile.handle,
            displayName: profile.displayName,
            description: profile.description,
            avatar: profile.avatar,
            banner: profile.banner,
            followsCount: profile.followsCount,
            followersCount: profile.followersCount,
            postsCount: profile.postsCount,
          },
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      } else {
        console.log('[authStore] No session, initializing without login');
        set({
          session: null,
          profile: null,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      }
    } catch (error) {
      console.error('[authStore] Initialize error:', error);
      set({
        session: null,
        profile: null,
        isLoading: false,
        isInitialized: true,
        error: null, // セッション復元エラーは無視
      });
    }
  },

  // エラークリア
  clearError: () => {
    set({ error: null });
  },
}));
